import type { ParentComponent, VoidComponent} from 'solid-js';
import { For, Show, createEffect, createMemo, createSignal } from 'solid-js';
import { LoadingIcon, standardIcons } from '../visual/index.ts';
import c from './FilterGroup.module.css';

type FilterGroupType = {
    Donut: ParentComponent<{
        busy?: boolean;
    }>;
    Search: VoidComponent<{
        autofocus?: boolean;
        value: string;
        onSearch: (value: string) => void;
    }>;
    Check: VoidComponent<{
        label: string;
        checked: boolean;
        onChange: (checked: boolean) => void;
    }>;
    Select: VoidComponent<{
        /** Empty string means no filter selected. */
        value: string;
        onSearch: (value: string) => void;
        label: string;
        options: { value: string; label?: string; }[];
    }>;
};

const commitSearchTermAfter = 300; // in ms

export const FilterGroup: FilterGroupType = {
    Donut: p => (
        <div class={c.donut}>
            <Show
                when={!p.busy}
                fallback={<LoadingIcon class={c.icon} />}
            >
                <standardIcons.filter class={c.icon} />
            </Show>
            {p.children}
        </div>
    ),
    Search: p => {
        const [searchTerm, setSearchTerm] = createSignal(p.value);
        createEffect(() => setSearchTerm(p.value));
        function clearSearch() {
            p.onSearch('');
        }
        function commitSearchTerm() {
            p.onSearch(searchTerm());
        }
        function handlePressDown(e: KeyboardEvent) {
            if (e.key === 'Enter') {
                commitSearchTerm();
            }
        }
        let timeout: number;
        function scheduleSearch() {
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
                commitSearchTerm();
            }, commitSearchTermAfter);
        }
        const showClearButton = createMemo(() => p.value !== '');
        return (
            <label class={[c.item, c.search].join(' ')}>
                <input
                    type='text'
                    value={searchTerm()}
                    onKeyDown={handlePressDown}
                    onInput={e => {
                        setSearchTerm(e.target.value);
                        scheduleSearch();
                    }}
                />
                <Show
                    when={showClearButton()}
                    fallback={
                        <standardIcons.search class={c.icon} />
                    }
                >
                    <standardIcons.clear
                        class={[c.icon, c.clearable].join(' ')}
                        onClick={clearSearch}
                    />
                </Show>
            </label>
        );
    },
    Check: p => (
        <label class={[c.item, c.check].join(' ')}>
            <span>
                {p.label}
            </span>
            <input
                type='checkbox'
                checked={p.checked}
                onChange={e => p.onChange(e.target.checked)}
            />
        </label>
    ),
    Select: p => (
        <label class={[c.item, c.check].join(' ')}>
            <span>
                {p.label}
            </span>
            <select
                class={[c.item, c.select].join(' ')}
                onChange={e => p.onSearch(e.currentTarget.value)}
            >
                <For each={p.options}>
                    {option => (
                        <option
                            value={option.value}
                            selected={option.value === p.value}
                        >
                            {option.label ?? option.value}
                        </option>
                    )}
                </For>
            </select>
        </label>
    ),
};
